import { Scheduling, TariffGroupAttribute } from '@src/core/buyerApiClient';

import {
  buildAttributeOptions,
  isActiveAttribute,
  orderedAttributeTypes,
} from '@src/domains/products/features/product-booking/core/attributes/buildAttributeOptions';
import { getSelectedAttributeValue } from '@src/domains/products/features/product-booking/core/attributes/selectedAttributes';
import type {
  AttributeOptionGroup,
  SelectedProductAttribute,
  TariffGroupAttributeType,
} from '@src/domains/products/features/product-booking/types/attribute';

const getTariffGroupIdFromScheduling = (scheduling: Scheduling): string | undefined => {
  const id = scheduling.tariffGroup.id?.trim();
  return id || undefined;
};

export const getTariffGroupIdsFromSchedulings = (schedulings: Scheduling[]): string[] => {
  const ids = new Set<string>();

  for (const scheduling of schedulings) {
    const id = getTariffGroupIdFromScheduling(scheduling);
    if (id) {
      ids.add(id);
    }
  }

  return [...ids];
};

export const filterAttributesByTariffGroupIds = (
  attributes: TariffGroupAttribute[],
  groupIds: Iterable<string>,
): TariffGroupAttribute[] => {
  const allowed = new Set(groupIds);

  return attributes.filter((attribute) => isActiveAttribute(attribute) && allowed.has(attribute.tariffGroupId));
};

const getTariffGroupIdsForAttribute = (
  attributes: TariffGroupAttribute[],
  type: TariffGroupAttributeType,
  value: string,
  restrictToGroupIds: Iterable<string>,
): string[] => {
  const normalized = value.trim();
  if (!normalized) {
    return [];
  }

  const allowed = new Set(restrictToGroupIds);
  const ids = new Set<string>();

  for (const attribute of attributes) {
    if (
      !isActiveAttribute(attribute) ||
      attribute.type !== type ||
      attribute.value.trim() !== normalized ||
      !allowed.has(attribute.tariffGroupId)
    ) {
      continue;
    }

    ids.add(attribute.tariffGroupId);
  }

  return [...ids];
};

const filterSchedulingsByTariffGroupIds = (schedulings: Scheduling[], groupIds: Iterable<string>): Scheduling[] => {
  const allowed = new Set(groupIds);

  return schedulings.filter((scheduling) => {
    const id = getTariffGroupIdFromScheduling(scheduling);

    return !!id && allowed.has(id);
  });
};

export const filterSchedulingsByAttribute = (
  schedulings: Scheduling[],
  attributes: TariffGroupAttribute[],
  type: TariffGroupAttributeType,
  value: string,
): Scheduling[] => {
  const reachableGroupIds = getTariffGroupIdsFromSchedulings(schedulings);
  const matchingGroupIds = getTariffGroupIdsForAttribute(attributes, type, value, reachableGroupIds);

  if (!matchingGroupIds.length) {
    return schedulings;
  }

  return filterSchedulingsByTariffGroupIds(schedulings, matchingGroupIds);
};

export const resolveSchedulingsForAttributes = (
  schedulings: Scheduling[],
  attributes: TariffGroupAttribute[],
  selected: SelectedProductAttribute[],
): Scheduling[] => {
  let resolved = schedulings;

  for (const { type, value } of selected) {
    if (!value?.trim()) {
      continue;
    }

    resolved = filterSchedulingsByAttribute(resolved, attributes, type, value);
  }

  return resolved;
};

export const buildAttributeSlotsForSchedulings = (
  attributes: TariffGroupAttribute[],
  schedulings: Scheduling[],
  selected: SelectedProductAttribute[],
): AttributeOptionGroup[] => {
  const reachableAttributes = filterAttributesByTariffGroupIds(
    attributes,
    getTariffGroupIdsFromSchedulings(schedulings),
  );

  if (!reachableAttributes.length) {
    return [];
  }

  const slots: AttributeOptionGroup[] = [];
  let remaining = schedulings;

  for (const type of orderedAttributeTypes(reachableAttributes)) {
    const scoped = filterAttributesByTariffGroupIds(attributes, getTariffGroupIdsFromSchedulings(remaining));
    const options = buildAttributeOptions(scoped, type);

    if (!options.length) {
      continue;
    }

    slots.push({ type, options });

    const value = getSelectedAttributeValue(selected, type);
    if (value && options.some((option) => option.value === value)) {
      remaining = filterSchedulingsByAttribute(remaining, attributes, type, value);
    }
  }

  return slots;
};

export const pruneSelectedAttributes = (
  attributes: TariffGroupAttribute[],
  schedulings: Scheduling[],
  selected: SelectedProductAttribute[],
): SelectedProductAttribute[] => {
  if (!selected.length) {
    return selected;
  }

  const slots = buildAttributeSlotsForSchedulings(attributes, schedulings, selected);

  return selected.filter((attribute) =>
    slots.some(
      (slot) => slot.type === attribute.type && slot.options.some((option) => option.value === attribute.value),
    ),
  );
};

export const schedulingsForTariffMountWithAttributes = (
  availables: Scheduling[],
  unavailables: Scheduling[],
  slots: AttributeOptionGroup[],
  selected: SelectedProductAttribute[],
): Scheduling[] => {
  const hasPendingSlot = slots.some((slot) => !getSelectedAttributeValue(selected, slot.type)?.trim());

  if (hasPendingSlot) {
    return availables;
  }

  return [...availables, ...unavailables];
};
